import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { FIRMWARE_TABS } from "../../../cloud-connect/ota-update/constants";
import { ListItem, TabType } from "@src/types/ota-update";
import {
  updateModalsCloseDrawerModal,
  updateShowOtaCreateBackupModal,
} from "@src/reducers/your-reducer";
import { useI18N } from "@src/language/language-context";

interface UseOTAFirmwareHistoryProps {
  setActiveOTAHistoryTab: React.Dispatch<React.SetStateAction<TabType>>;
  setIsFirmwareLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsBackupLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useOTAFirmwareHistory = ({
  setActiveOTAHistoryTab,
  setIsFirmwareLoading,
  setIsBackupLoading,
}: UseOTAFirmwareHistoryProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<null | ListItem>(null);
  const dispatch = useDispatch();
  const i18n = useI18N();

  const texts = {
    firmwareVersionsText: i18n("modules.otaHistory.firmwareVersionsText"),
    firmwareBackupsText: i18n("modules.otaHistory.firmwareBackupsText"),
    uploadFirmwareReleaseText: i18n(
      "modules.otaHistory.uploadFirmwareReleaseText",
    ),
    uploadFirmwareBackupText: i18n(
      "modules.otaHistory.uploadFirmwareBackupText",
    ),
    createBackupText: i18n("modules.otaHistory.createBackupText"),
    createBackupDisabledText: i18n(
      "modules.otaHistory.createBackupDisabledText",
    ),
  };

  const tabLabels: Record<TabType, string> = {
    [FIRMWARE_TABS.VERSIONS]: texts.firmwareVersionsText,
    [FIRMWARE_TABS.BACKUPS]: texts.firmwareBackupsText,
  };

  const labelToKey: Record<string, TabType> = {
    [texts.firmwareVersionsText]: FIRMWARE_TABS.VERSIONS,
    [texts.firmwareBackupsText]: FIRMWARE_TABS.BACKUPS,
  };

  const handleTabChange = (label: string) => {
    const key = labelToKey[label];

    if (!key) {
      return;
    }

    switch (key) {
      case FIRMWARE_TABS.VERSIONS:
        setIsFirmwareLoading(true);
        break;

      case FIRMWARE_TABS.BACKUPS:
        setIsBackupLoading(true);
        break;
    }

    setActiveOTAHistoryTab(key);
  };

  const closeFirmwareModal = () => {
    setIsModalOpen(false);
  };

  const showOTAFirmwareVersionDrawer = () => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: true,
        id: "otaFirmwareVersionDrawerModal",
      }),
    );
  };

  const showOTAFirmwareBackupDrawer = () => {
    dispatch(updateShowOtaCreateBackupModal(true));
  };

  const getPermission = (activeOTAHistoryTab: TabType) => {
    return activeOTAHistoryTab === FIRMWARE_TABS.VERSIONS
      ? "manage-firmware"
      : "manage-firmware-backup";
  };

  return {
    texts,
    tabLabels,
    isModalOpen,
    setIsModalOpen,
    selectedRow,
    setSelectedRow,
    handleTabChange,
    closeFirmwareModal,
    showOTAFirmwareVersionDrawer,
    showOTAFirmwareBackupDrawer,
    getPermission,
  };
};
